import { useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const FUNNEL = [
  ["total_retrieved", "Recuperados"], ["real_duplicates_removed", "Duplicados reales"], ["limit_trimmed", "Recortados por límite"],
  ["cheap_filtered_count", "Filtros baratos"], ["preselected_count", "Preseleccionados → Apify"], ["enriched_count", "Enriquecidos"],
  ["qualified_count", "Cualificados"], ["opportunity_count", "Oportunidades"],
];
const Row = ({ k, v }) => <div className="flex justify-between gap-3 text-xs"><span className="text-[var(--ca-subtle)]">{k}</span><span className="text-right">{v ?? "NO REGISTRADO"}</span></div>;
const Box = ({ title, children }) => (
  <div className="rounded-lg border border-[var(--ca-line)] p-3"><p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--ca-subtle)]">{title}</p>{children}</div>
);

export const SearchList = ({ searches }) => {
  const [open, setOpen] = useState(null);
  const [diag, setDiag] = useState(null);
  const load = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); setDiag(null);
    try { setDiag((await api.get(`/admin/search-jobs/${id}`)).data); } catch (e) { toast.error(apiError(e)); setOpen(null); }
  };
  if (!searches?.length) return <p className="text-xs text-[var(--ca-subtle)]">Sin búsquedas.</p>;
  return (
    <div className="space-y-2" data-testid="admin-search-list">
      {searches.map((s) => (
        <div key={s.job_id}>
          <button type="button" onClick={() => load(s.job_id)} data-testid={`admin-search-${s.job_id}`} className="flex w-full justify-between text-left text-xs hover:text-[var(--ca-gold)]">
            <span>{s.avatar_name || "—"} · {s.status}</span><span className="text-[var(--ca-subtle)]">{open === s.job_id ? "Ocultar" : "Diagnóstico"}</span>
          </button>
          {open === s.job_id && (diag ? <Diagnostic d={diag} /> : <Loader2 className="my-2 animate-spin" size={16} />)}
        </div>
      ))}
    </div>
  );
};

// v26h · Motor indirecto: con qué palabras se buscaron lugares, qué devolvió cada una y qué quitó cada filtro.
const REMOVED = { fuera_de_mercado: "Fuera de mercado", misma_marca: "Misma marca", vetadas: "Vetadas", ya_usados: "Ya usados", tamano: "Tamaño" };
const list = (xs) => (xs && xs.length ? xs.map((x) => `@${x}`).join(" · ") : null);
const IndirectBox = ({ ind }) => (
  <Box title="Motor indirecto (lugares)">
    <Row k="Tipo / ruta" v={[ind.route_class, ind.ruta].filter(Boolean).join(" · ") || null} />
    {ind.error && <Row k="Error del motor" v={ind.error} />}
    <Row k={ind.place_words_fallback ? "Palabras (respaldo: el mapa no dio)" : "Palabras buscadas"} v={(ind.place_words || []).join(" · ") || "ninguna"} />
    {Object.entries(ind.per_word || {}).map(([w, n]) => <Row key={w} k={`  «${w}»`} v={typeof n === "number" ? `${n} cuentas` : n} />)}
    <Row k="Cuentas propuestas por la IA" v={list(ind.proposed_accounts)} />
    <Row k="Cuentas de referencia" v={list(ind.references)} />
    <Row k="Lugares elegidos" v={list(ind.places) || "ninguno"} />
    {Object.entries(ind.places_removed || {}).filter(([, v]) => v.length).map(([k, v]) => (
      <Row key={k} k={`Quitados · ${REMOVED[k] || k} (${(ind.places_info || {})[k] ?? v.length})`} v={list(v)} />
    ))}
    <Row k="Candidatas / despensa" v={`${ind.candidates ?? "—"} / ${ind.pending ?? "—"}`} />
    {ind.mismo_oficio > 0 && <Row k={`Quitados · Mismo oficio (${ind.mismo_oficio})`} v={list(ind.mismo_oficio_ejemplos)} />}
    <Row k="TikHub llamadas / errores" v={`${ind.tikhub_calls ?? "—"} / ${ind.tikhub_errors ?? "—"}`} />
    {ind.plan_b && <Row k="Plan B" v={`${ind.plan_b.via} · ${ind.plan_b.found} encontradas`} />}
  </Box>
);

// v26i · Cómo lo pensó la IA antes de buscar: quién, si se presenta así (ruta), dónde, y a quién no busca.
const ReasoningBox = ({ r, rc, same, trades }) => (
  <Box title="Cómo lo ha pensado la IA">
    <Row k="Quién" v={r?.quien || null} />
    <Row k="Situación" v={r?.situacion || null} />
    <Row k="¿Se presenta así en su bio?" v={r && "se_presenta_asi" in r ? (r.se_presenta_asi ? "Sí" : "No") : null} />
    <Row k="Por qué" v={r?.por_que_ruta || null} />
    <Row k="Ruta elegida" v={rc ? `${rc.ruta || "—"} · ${rc.tipo || ""} · decide: ${{ ia: "IA", autocorreccion: "autocorrección" }[rc.fuente] || "reglas"}` : null} />
    {(r?.donde || []).map((x, i) => <Row key={i} k={`Dónde · ${x.lugar}`} v={[x.por_que, (x.cuentas || []).map((a) => `@${a}`).join(" ")].filter(Boolean).join(" · ") || "—"} />)}
    <Row k="Palabras para los lugares" v={(r?.lugares || []).join(" · ") || null} />
    <Row k="Quién NO es" v={(r?.no_es || []).join(" · ") || null} />
    <Row k="Oficios que no son clientes" v={(trades || r?.oficios_excluidos || []).join(" · ") || null} />
    {same && <Row k={same.mode === "ultimo_recurso" ? `Mismo oficio · último recurso (${same.count})` : `Quitados por ser del mismo oficio (${same.count})`} v={(same.examples || []).map((u) => `@${u}`).join(" · ") || "—"} />}
  </Box>
);

const FIX = { buscar_en_lugares: "Buscar en lugares", otros_lugares: "Otros lugares", buscar_por_bio: "Buscar por la bio",
  otras_palabras: "Otras palabras", otro_perfil: "Otro perfil" };
const STOP = { sin_tiempo: "sin tiempo", sin_opciones: "sin más opciones" };
const AutofixBox = ({ a }) => (
  <Box title={`Autocorrección · ${a.resuelta ? "encontró perfiles" : "sin perfiles"}`}>
    {(a.intentos || []).map((x, i) => (
      <div key={i} className="mb-1.5">
        <Row k={`Intento ${x.intento} · ${FIX[x.estrategia] || STOP[x.parado] || x.parado || "—"}`}
          v={x.estrategia ? `${x.encontradas ?? "—"} encontradas · ${x.validas ?? 0} válidas${x.segundos != null ? ` · ${x.segundos} s` : ""}${x.parado ? ` · ${STOP[x.parado] || x.parado}` : ""}${x.error ? ` · ${x.error}` : ""}` : "parado"} />
        {x.estrategia && <Row k="Diagnóstico" v={`${x.punto || ""}${x.diagnostico ? ` · ${x.diagnostico}` : ""} · decide: ${x.fuente === "ia" ? "IA" : "reglas"}`} />}
        {x.por_que && <Row k="Por qué" v={x.por_que} />}
        {x.palabras?.length > 0 && <Row k="Palabras" v={x.palabras.join(" · ")} />}
      </div>
    ))}
  </Box>
);

const Diagnostic = ({ d }) => {
  const f = d.funnel || {};
  const ai = d.plan?.ai;
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid="admin-search-diagnostic">
      <Box title="Resumen">
        <Row k="Estado" v={d.summary?.status} /><Row k="Duración" v={d.duration_ms != null ? `${(d.duration_ms / 1000).toFixed(1)} s` : null} />
        <Row k="Objetivo" v={d.objective} /><Row k="Perfil" v={d.summary?.avatar_name} />
        <Row k="Mercado" v={d.summary?.market?.target_market} /><Row k={d.cost_estimated ? "Coste proveedores (estimado)" : "Coste proveedores"} v={d.cost != null ? `${d.cost} USD` : "desconocido"} />
      </Box>
      <Box title="Plan">
        <Row k="Origen" v={d.plan?.source} />
        <Row k="IA" v={ai ? `${ai.provider} · ${ai.model_returned} · ${ai.reasoning_effort} · ${ai.total_tokens} tok` : "—"} />
        <Row k="Propuestas" v={(d.plan?.proposed || []).join(" · ")} /><Row k="Ejecutadas" v={(d.plan?.executed || []).join(" · ")} />
        <Row k="Límites (pool / Apify)" v={`${d.plan?.limits?.pool ?? "—"} / ${d.plan?.limits?.enrichment ?? "—"}`} />
      </Box>
      <Box title="Discovery por query">
        {(d.queries || []).map((q, i) => (
          <Row key={i} k={`${q.query}${q.recovery ? " (recuperación)" : ""}`} v={`${q.returned}${q.data_null ? " · data:null" : ""}${q.error ? ` · ${q.error}` : ""}${q.duration_ms != null ? ` · ${q.duration_ms} ms` : ""}`} />
        ))}
      </Box>
      {(d.reasoning || d.route_class) && <ReasoningBox r={d.reasoning} rc={d.route_class} same={d.same_trade} trades={d.excluded_trades} />}
      {d.indirect && <IndirectBox ind={d.indirect} />}
      {d.autocorrect && <AutofixBox a={d.autocorrect} />}
      <Box title="Funnel">{FUNNEL.map(([k, l]) => <Row key={k} k={l} v={f[k]} />)}</Box>
      <Box title="Proveedores">
        <Row k="TikHub llamadas" v={d.provider_calls?.tikhub} />
        {(d.apify_runs || []).map((r, i) => <Row key={i} k={`Apify ${r.actor}`} v={`${r.calls} llam. · ${r.profiles_sent} env. · ${r.profiles_enriched} enriq. · coste ${r.cost_usd ?? "desconocido"}${r.error ? ` · ${r.error}` : ""}`} />)}
        {(d.errors || []).map((e, i) => <Row key={`e${i}`} k="Error" v={`${e.provider || ""} ${e.status || ""}`} />)}
      </Box>
      <Box title="Contexto congelado">
        <Row k="Versión resumen" v={d.context?.business_summary_version} /><Row k="Perfil" v={d.context?.prospect_profile_id} />
        <Row k="Entidad" v={d.context?.entity_type} /><Row k="Mercado" v={d.context?.market?.target_market || "sin restricción"} />
        <Row k="Identidades" v={(d.search_identities || []).map((s) => `${s.query} [${s.entity_type}]`).join(" · ") || null} />
      </Box>
      <Box title="Preselección / Apify / Cualificación">
        <Row k="Preselección" v={d.preselection ? `${d.preselection.source} · ENRICH ${d.preselection.enrich} · SKIP ${d.preselection.skip} · duros ${d.preselection.hard_filtered}` : null} />
        <Row k="Apify válidos / errores" v={(d.apify_runs || []).map((r) => `${r.valid_profiles ?? r.profiles_enriched ?? "—"} / ${(r.error_items || []).length}`).join(" · ") || null} />
        <Row k="Cualificación" v={d.qualification?.source} />
        <Row k="Estados" v={d.qualification?.states ? Object.entries(d.qualification.states).map(([k, v]) => `${k} ${v}`).join(" · ") : null} />
        <Row k="Mostradas / reserva" v={d.qualification?.shown != null ? `${d.qualification.shown} / ${d.qualification.reserved}` : null} />
        {Object.entries(d.ai_by_stage || {}).map(([k, s]) => <Row key={k} k={`IA ${k}`} v={`${s.calls} llam. · ${s.tokens} tok · ${s.cost_usd} USD`} />)}
      </Box>
      <Box title={`Candidatos (${(d.candidate_trace || []).length})`}>
        <div className="max-h-48 overflow-y-auto">
          {(d.candidate_trace || []).map((c, i) => (
            <Row key={i} k={`@${c.username}`} v={`${c.stage || c.enrich_status} · ${c.preselection?.decision || "—"} · ${c.qualification_state || c.depth_decision || c.selection || "—"}${c.priority ? `/${c.priority}` : ""}${c.presentation ? ` · ${c.presentation}` : ""}${c.reason_code ? ` · ${c.reason_code}` : ""}`} />
          ))}
        </div>
      </Box>
    </div>
  );
};
